import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { Link as RouterLink } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import toast from "react-hot-toast";
import lottoRecoIcon from "../../../assets/images/lottoReco-icon.png";

import { useAuth } from "features/auth";
import { useAuditCodes } from "features/users/hooks/queries/auditCodeQueries";
import { buildAuditCodeAccessUrl } from "features/users/utils/auditCodeUrls";
import type { AuditCodeListItem } from "services/auditCodeService";
import { createWelcomeGuidePdf } from "../services/welcomeGuidePdf";

type StoreWelcomePanelProps = {
  store: {
    id: string;
    name?: string;
  };
  compact?: boolean;
  onDismiss?: () => void;
  onOpenGuide?: () => void;
};

export default function StoreWelcomePanel({ store, compact = false, onDismiss, onOpenGuide }: StoreWelcomePanelProps) {
  const { state: authState } = useAuth();
  const { data: auditCodes = [], isLoading, isError } = useAuditCodes(store.id);
  const activeCodes = useMemo(
    () => auditCodes.filter((location) => location.active && location.code),
    [auditCodes]
  );
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const logoImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!activeCodes.length) {
      setSelectedLocationId("");
      return;
    }

    if (!activeCodes.some((location) => location.store_location_id === selectedLocationId)) {
      setSelectedLocationId(activeCodes[0].store_location_id);
    }
  }, [activeCodes, selectedLocationId]);

  const selectedLocation = activeCodes.find(
    (location) => location.store_location_id === selectedLocationId
  );
  const scannerUrl = selectedLocation?.code
    ? buildAuditCodeAccessUrl(selectedLocation.code)
    : "";

  const handleStartAudit = () => {
    if (scannerUrl) {
      window.open(scannerUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownloadGuide = () => {
    if (!selectedLocation || !scannerUrl || !qrCanvasRef.current || !logoImageRef.current?.complete) {
      toast.error("The welcome guide is not ready yet.");
      return;
    }

    createWelcomeGuidePdf({
      storeName: store.name || "LottoReco Store",
      recipientName: authState.user?.name || "Store Owner",
      locationName: selectedLocation.store_location_name,
      scannerUrl,
      qrCodeDataUrl: qrCanvasRef.current.toDataURL("image/png"),
      logoDataUrl: imageToDataUrl(logoImageRef.current),
    });
  };

  if (compact) {
    return (
      <Box
        sx={{
          alignItems: { xs: "flex-start", md: "center" },
          bgcolor: "#EFF6FF",
          border: "1px solid #BFDBFE",
          borderRadius: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          justifyContent: "space-between",
          mb: 2,
          px: 2,
          py: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ color: "#1E3A5F", fontSize: 15, fontWeight: 800 }}>
            Keep your cashiers ready to scan
          </Typography>
          <Typography sx={{ color: "#475569", fontSize: 13, mt: 0.25 }}>
            Download and post the store-specific QR guide at the cash register.
          </Typography>
        </Box>
        {onOpenGuide && (
          <Button
            variant="text"
            onClick={onOpenGuide}
            sx={{ color: "#1E5F91", fontWeight: 800, textTransform: "none", whiteSpace: "nowrap" }}
          >
            View getting started guide
          </Button>
        )}
        <GuideActions
          activeCodes={activeCodes}
          isLoading={isLoading}
          scannerUrl={scannerUrl}
          selectedLocationId={selectedLocationId}
          onLocationChange={setSelectedLocationId}
          onDownload={handleDownloadGuide}
          onStart={handleStartAudit}
          compact
        />
        {scannerUrl && <HiddenQrCode ref={qrCanvasRef} value={scannerUrl} />}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        border: "1px solid #DDE7EE",
        borderRadius: 3,
        boxShadow: "0 10px 32px rgba(16, 42, 67, 0.06)",
        color: "#24364B",
        fontFamily: '"Bitter", Georgia, serif',
        overflow: "hidden",
        p: { xs: 2.5, sm: 4, md: 5 },
        "& *:not(svg)": { fontFamily: "inherit" },
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid #E3EAF0",
          pb: { xs: 3, md: 3.5 },
        }}
      >
        <Box sx={{ alignItems: "flex-start", display: "flex", gap: 2, justifyContent: "space-between" }}>
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, minWidth: 0 }}>
            <Typography component="h1" id="getting-started-guide-title" sx={{ color: "#102A43", fontSize: { xs: 26, md: 33 }, fontWeight: 850, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
              LottoReco Getting Started Guide
            </Typography>
            <Tooltip title="Download welcome guide">
              <span>
                <IconButton
                  aria-label="Download welcome guide"
                  disabled={!scannerUrl || isLoading}
                  onClick={handleDownloadGuide}
                  size="small"
                  sx={{ color: "#087A4C", flex: "0 0 auto", "&:hover": { bgcolor: "#E7F8EF" } }}
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {onDismiss && (
            <Tooltip title="Close getting started guide">
              <IconButton aria-label="Close getting started guide" onClick={onDismiss} sx={{ color: "#52677A", flex: "0 0 auto" }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ color: "#334E68", fontSize: { xs: 15, md: 17 }, lineHeight: 1.7, maxWidth: 780, mt: 1.25 }}>
          You can start using LottoReco today to audit your scratcher inventory <Box component="strong" sx={{ color: "#102A43" }}>without writing every ticket number in a spreadsheet</Box>.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: "1px solid #E3EAF0", display: "grid", gap: { xs: 3, lg: 4 }, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 310px" }, py: { xs: 3, md: 4 } }}>
        <Box>
          <Typography sx={{ color: "#0B8F5B", fontSize: 12, fontWeight: 850, letterSpacing: "0.08em", textTransform: "uppercase" }}>Step 1 · Get started</Typography>
          <Typography sx={{ color: "#102A43", fontSize: 21, fontWeight: 850, mt: 0.5 }}>Start your first audit</Typography>
          <Typography sx={{ color: "#486581", fontSize: 14, mt: 0.5 }}>Use any phone or tablet with a camera. It only takes a few minutes.</Typography>

          <Stack spacing={1.4} sx={{ mt: 2.5 }}>
            {[
              {
                title: "Open the scanner",
                copy: (
                  <>
                    Click{" "}
                    <Button
                      variant="text"
                      disabled={!scannerUrl || isLoading}
                      onClick={handleStartAudit}
                      sx={{ color: "#087A4C", fontSize: "inherit", fontWeight: 800, lineHeight: "inherit", minWidth: 0, p: 0, textDecoration: "underline", textTransform: "none", verticalAlign: "baseline" }}
                    >
                      Start first audit
                    </Button>{" "}
                    or scan the QR code at the register.
                  </>
                ),
              },
              {
                title: "Enter the cashier PIN",
                copy: (
                  <>
                    Use the default PIN <strong>123456</strong>. To manage user accounts or change login credentials and cashier PINs, {" "}
                    <Box
                      component={RouterLink}
                      to={`/store/${store.id}/users`}
                      sx={{ color: "#087A4C", fontWeight: 800, textDecoration: "underline" }}
                    >
                      click here
                    </Box>
                    .
                  </>
                ),
              },
              { title: "Scan your scratcher packs", copy: "Scan the scratcher packs currently in your display." },
            ].map((step, index) => (
              <Box key={step.title} sx={{ alignItems: "flex-start", borderBottom: index < 2 ? "1px solid #E9EFF4" : "none", display: "flex", gap: 1.5, px: 0.5, py: 1.25 }}>
                <Box sx={{ alignItems: "center", bgcolor: "#E7F8EF", borderRadius: "50%", color: "#087A4C", display: "flex", flex: "0 0 32px", fontSize: 13, fontWeight: 850, height: 32, justifyContent: "center" }}>
                  {index + 1}
                </Box>
                <Box>
                  <Typography sx={{ color: "#102A43", fontSize: 14.5, fontWeight: 800 }}>{step.title}</Typography>
                  <Typography sx={{ color: "#486581", fontSize: 13.5, lineHeight: 1.55, mt: 0.2 }}>{step.copy}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {isError && <Alert severity="error" sx={{ mt: 3 }}>The scanner QR code could not be loaded. Refresh the page or manage audit codes under Store Users.</Alert>}
          {!isLoading && !isError && activeCodes.length === 0 && (
            <Alert severity="warning" sx={{ mt: 3 }}>No active scanner code is available. Generate one under Manage Users before printing the guide.</Alert>
          )}

        </Box>

        <Box sx={{ alignItems: "center", borderLeft: { lg: "1px solid #E3EAF0" }, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 310, pl: { lg: 4 }, py: { xs: 1, lg: 0 } }}>
          {isLoading ? (
            <CircularProgress size={28} />
          ) : scannerUrl ? (
            <>
              <Typography sx={{ color: "#102A43", fontSize: 15, fontWeight: 800, textAlign: "center" }}>
                Make audits easy for every cashier
              </Typography>
              <Typography sx={{ color: "#486581", fontSize: 12.5, lineHeight: 1.5, mb: 1.4, mt: 0.35, textAlign: "center" }}>
                Place the printed QR guide next to your scratcher display case for quick access.
              </Typography>
              <Box sx={{ border: "1px solid #E6ECF1", borderRadius: 2, p: 1 }}>
                <QRCodeCanvas ref={qrCanvasRef} value={scannerUrl} size={190} level="M" marginSize={2} />
              </Box>
              <Typography sx={{ color: "#102A43", fontSize: 13.5, fontWeight: 800, mt: 1.4, textAlign: "center" }}>
                Using another device?
              </Typography>
              <Typography sx={{ color: "#486581", fontSize: 12.5, lineHeight: 1.5, mt: 0.25, textAlign: "center" }}>
                Scan this QR code with its camera.
              </Typography>
              <Typography sx={{ color: "#7A8C9C", fontSize: 12, mt: 0.5 }}>{selectedLocation?.store_location_name}</Typography>
              <Box sx={{ textAlign: "center", width: "100%" }}>
                {activeCodes.length > 1 && (
                  <FormControl size="small" sx={{ mt: 1.25, minWidth: 190 }}>
                    <Select
                      aria-label="Scanning location"
                      value={selectedLocationId}
                      onChange={(event) => setSelectedLocationId(String(event.target.value))}
                    >
                      {activeCodes.map((location) => (
                        <MenuItem key={location.store_location_id} value={location.store_location_id}>
                          {location.store_location_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </>
          ) : (
            <Typography sx={{ color: "#64748B", fontSize: 13, textAlign: "center" }}>QR code unavailable</Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ pt: { xs: 3, md: 4 } }}>
        <Typography sx={{ color: "#243B53", fontSize: 15, lineHeight: 1.75 }}>
          <strong>That is it.</strong> LottoReco will record your inventory and create a digital history of each audit. You can then use the <strong>LottoReco mobile app or website</strong> to see total inventory sales <strong>by shift or by day</strong>.
        </Typography>

        <Typography component="h2" sx={{ color: "#102A43", fontSize: 21, fontWeight: 850, mt: 3 }}>Next Steps</Typography>
        <Typography sx={{ color: "#243B53", fontSize: 15, lineHeight: 1.7, mt: 0.75 }}>
          LottoReco provides end-to-end reconciliation by verifying that:
        </Typography>
        <Box component="ul" sx={{ color: "#243B53", fontSize: 14.5, lineHeight: 1.7, mb: 0, mt: 1.25, pl: 2.5 }}>
          <li>Every activated and returned scratcher pack is accounted for.</li>
          <li>POS scratcher sales match the inventory removed.</li>
          <li>Lottery and scratcher payouts match the payouts recorded in your POS.</li>
          <li>Lotto sales reported by your POS match the lottery portal.</li>
        </Box>

        <Typography sx={{ color: "#243B53", fontSize: 15, lineHeight: 1.7, mt: 2.5 }}>
          When you are ready, we will help connect your:
        </Typography>
        <Box component="ul" sx={{ color: "#243B53", fontSize: 14.5, fontWeight: 700, lineHeight: 1.7, mb: 0, mt: 0.75, pl: 2.5 }}>
          <li>POS system</li>
          <li>State lottery account</li>
        </Box>

        <Typography sx={{ borderTop: "1px solid #E3EAF0", color: "#243B53", fontSize: 14.5, lineHeight: 1.7, mt: 2.5, pt: 2.25 }}>
          At any point, {" "}
          <Box component="a" href="https://wa.me/16508883316" target="_blank" rel="noreferrer" sx={{ color: "#087A4C", fontWeight: 800, textDecoration: "underline" }}>
            contact us on WhatsApp at 650-888-3316
          </Box>{" "}
          and we will help you get started.
        </Typography>
      </Box>
      <img ref={logoImageRef} src={lottoRecoIcon} alt="" aria-hidden="true" style={{ display: "none" }} />
    </Box>
  );
}

type GuideActionsProps = {
  activeCodes: AuditCodeListItem[];
  isLoading: boolean;
  scannerUrl: string;
  selectedLocationId: string;
  onLocationChange: (value: string) => void;
  onDownload: () => void;
  onStart: () => void;
  compact?: boolean;
  showStart?: boolean;
};

function GuideActions({
  activeCodes = [],
  isLoading,
  scannerUrl,
  selectedLocationId,
  onLocationChange,
  onDownload,
  onStart,
  compact = false,
  showStart = true,
}: GuideActionsProps) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ alignItems: { sm: "center" } }}>
      {(activeCodes?.length || 0) > 1 && (
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select
            aria-label="Scanning location"
            value={selectedLocationId}
            onChange={(event) => onLocationChange(String(event.target.value))}
          >
            {activeCodes?.map((location) => (
              <MenuItem key={location.store_location_id} value={location.store_location_id}>
                {location.store_location_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {!compact && showStart && (
        <Button variant="contained" startIcon={<QrCodeScannerIcon />} disabled={!scannerUrl || isLoading} onClick={onStart} sx={{ fontWeight: 800, textTransform: "none" }}>
          Start first audit
        </Button>
      )}
      <Button variant={compact ? "contained" : "outlined"} startIcon={<DownloadIcon />} disabled={!scannerUrl || isLoading} onClick={onDownload} sx={{ fontWeight: 800, textTransform: "none", whiteSpace: "nowrap" }}>
        Download welcome guide
      </Button>
    </Stack>
  );
}

const HiddenQrCode = forwardRef<HTMLCanvasElement, { value: string }>(({ value }, ref) => (
  <Box aria-hidden="true" sx={{ height: 0, overflow: "hidden", position: "absolute", width: 0 }}>
    <QRCodeCanvas ref={ref} value={value} size={512} level="M" marginSize={4} />
  </Box>
));

HiddenQrCode.displayName = "HiddenQrCode";

const imageToDataUrl = (image: HTMLImageElement) => {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  canvas.getContext("2d")?.drawImage(image, 0, 0);
  return canvas.toDataURL("image/png");
};
